import React, { useMemo } from 'react';
import {
  AlertTriangle,
  BarChart3,
  Gauge,
  Landmark,
  ShieldCheck,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { AnalysisItem } from '../lib/api/projectAnalysis';
import { buildAnalysisInsight } from '../lib/analysisInsights';
import { formatCompactMoney, formatMoney, formatPercentDisplay } from '../lib/numberFormat';

const SECTION_COLORS = ['#0a4d29', '#0f766e', '#0369a1', '#7c3aed', '#b45309', '#be123c', '#334155', '#0e7490'];

const toneClass = (tone: 'danger' | 'warning' | 'info') => {
  if (tone === 'danger') return 'border-rose-200 bg-rose-50 text-rose-900';
  if (tone === 'warning') return 'border-amber-200 bg-amber-50 text-amber-950';
  return 'border-sky-200 bg-sky-50 text-sky-950';
};

const MoneyValue: React.FC<{ amount: number; className?: string }> = ({ amount, className = '' }) => (
  <>
    <span className={`sm:hidden ${className}`}>{formatCompactMoney(amount)}</span>
    <span className={`hidden sm:inline ${className}`} dir="ltr">
      {formatMoney(amount)}
    </span>
  </>
);

const KpiCard: React.FC<{
  label: string;
  amount?: number;
  value?: string;
  hint?: string;
  accent?: string;
}> = ({ label, amount, value, hint, accent = 'border-slate-200 bg-white' }) => (
  <div className={`rounded-xl border p-3 sm:p-4 shadow-sm min-w-0 overflow-hidden ${accent}`}>
    <p className="text-[11px] font-bold text-slate-500 mb-1 truncate">{label}</p>
    {amount != null ? (
      <>
        <p className="sm:hidden font-black text-[15px] text-slate-900 leading-snug break-words">{formatCompactMoney(amount)}</p>
        <p className="hidden sm:block font-mono font-black text-lg text-slate-900 leading-tight" dir="ltr">
          {formatMoney(amount)}
        </p>
      </>
    ) : (
      <p className="font-mono font-black text-[15px] sm:text-lg text-slate-900 leading-snug break-words" dir="ltr">
        {value}
      </p>
    )}
    {hint && <p className="text-[11px] text-slate-500 mt-1 leading-snug">{hint}</p>}
  </div>
);

interface Props {
  analysis: AnalysisItem;
}

const AnalysisInsights: React.FC<Props> = ({ analysis }) => {
  const insight = useMemo(() => buildAnalysisInsight(analysis), [analysis]);
  const sectionChart = insight.sections.filter((s) => s.calculated > 0).slice(0, 8);

  return (
    <div className="flex-1 overflow-y-auto overflow-x-hidden px-3 sm:px-4 lg:px-6 py-4 sm:py-5 space-y-4 sm:space-y-5" dir="rtl">
      {insight.alerts.length > 0 && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
          {insight.alerts.map((alert) => (
            <div key={alert.title} className={`rounded-xl border px-3 sm:px-4 py-3 flex gap-3 ${toneClass(alert.tone)}`}>
              <AlertTriangle size={18} className="shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-sm font-bold">{alert.title}</p>
                <p className="text-xs leading-relaxed mt-0.5 opacity-90">{alert.text}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-2 sm:gap-3">
        <KpiCard
          label="قیمت پیشنهادی"
          amount={insight.total}
          hint={analysis.currency ?? 'ریال'}
          accent="border-emerald-200 bg-emerald-50"
        />
        <KpiCard label="هزینه پایه" amount={insight.base} hint={`${insight.sections.length} بخش`} />
        <KpiCard
          label="سود خالص"
          amount={insight.netProfit}
          hint={`حاشیه ${formatPercentDisplay(insight.netMargin, 1)}٪`}
          accent={insight.netProfit < 0 ? 'border-rose-200 bg-rose-50' : 'border-amber-200 bg-amber-50'}
        />
        <KpiCard
          label="ضریب پیمانکار"
          value={insight.coefficient != null ? insight.coefficient.toFixed(4) : '—'}
          hint={insight.differencePct != null ? `${insight.differencePct >= 0 ? '+' : ''}${formatPercentDisplay(insight.differencePct, 2)} نسبت به برآورد` : 'برآورد را وارد کنید'}
          accent="border-teal-200 bg-teal-50"
        />
        <KpiCard
          label="ضریب سربه‌سر"
          value={insight.breakEvenCoefficient != null ? insight.breakEvenCoefficient.toFixed(4) : '—'}
          hint="سود صفر پس از بیمه و مالیات"
        />
        <KpiCard
          label="مارک‌آپ روی پایه"
          value={`${formatPercentDisplay(insight.markup, 1)}٪`}
          hint={`سود ناخالص ${formatPercentDisplay(insight.grossMargin, 1)}٪`}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        <section className="xl:col-span-5 bg-white rounded-xl border border-slate-200 shadow-sm p-3 sm:p-4 min-w-0">
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <BarChart3 size={18} className="text-emerald-800 shrink-0" />
            <h3 className="text-sm font-bold text-slate-800">آبشار قیمت پیشنهادی</h3>
          </div>
          <div className="space-y-2">
            {insight.waterfall.map((row) => (
              <div
                key={row.key}
                className={`flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between rounded-lg px-3 py-2.5 text-sm min-w-0 ${
                  row.tone === 'total'
                    ? 'bg-emerald-900 text-white'
                    : row.tone === 'minus'
                      ? 'bg-rose-50 text-rose-900'
                      : row.tone === 'add'
                        ? 'bg-slate-50 text-slate-800'
                        : 'bg-slate-800 text-white'
                }`}
              >
                <span className="font-bold leading-snug">{row.label}</span>
                <span className="font-black text-[15px] sm:text-sm sm:font-bold sm:font-mono self-end sm:self-auto tabular-nums">
                  {row.tone === 'minus' && row.amount > 0 ? '− ' : ''}
                  <MoneyValue amount={row.amount} />
                </span>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
            سود به روش «{insight.profitTypeLabel}» محاسبه شده. کسورات اضافی از سود کسر می‌شوند و در قیمت پیشنهادی نمی‌آیند.
          </p>
        </section>

        <section className="xl:col-span-7 bg-white rounded-xl border border-slate-200 shadow-sm p-3 sm:p-4 min-w-0">
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <TrendingUp size={18} className="text-emerald-800 shrink-0" />
            <h3 className="text-sm font-bold text-slate-800">توزیع بخش‌ها</h3>
          </div>
          {sectionChart.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
              <div className="lg:col-span-2 h-48 sm:h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={sectionChart} dataKey="calculated" nameKey="name" innerRadius={42} outerRadius={70} paddingAngle={2}>
                      {sectionChart.map((row, i) => (
                        <Cell key={row.id} fill={SECTION_COLORS[i % SECTION_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: number) => formatMoney(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="hidden lg:block lg:col-span-3 h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={sectionChart} layout="vertical" margin={{ right: 12, left: 8, top: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="name" width={88} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v: number) => formatMoney(v)} />
                    <Bar dataKey="calculated" radius={[0, 6, 6, 0]}>
                      {sectionChart.map((row, i) => (
                        <Cell key={row.id} fill={SECTION_COLORS[i % SECTION_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <ul className="lg:hidden space-y-1.5">
                {sectionChart.map((row, i) => (
                  <li key={row.id} className="flex items-center justify-between gap-2 text-sm">
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: SECTION_COLORS[i % SECTION_COLORS.length] }} />
                      <span className="truncate font-medium text-slate-700">{row.name}</span>
                    </span>
                    <span className="font-bold text-slate-900 shrink-0">{formatCompactMoney(row.calculated)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="h-40 flex items-center justify-center text-sm text-slate-400">بخشی برای نمودار وجود ندارد</div>
          )}
        </section>
      </div>

      <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-3 sm:px-4 py-3 border-b border-slate-100 flex items-center gap-2">
          <Landmark size={18} className="text-emerald-800 shrink-0" />
          <h3 className="text-sm font-bold text-slate-800">رتبه‌بندی بخش‌ها</h3>
        </div>
        <div className="lg:hidden divide-y divide-slate-100">
          {insight.sections.map((row) => (
            <div key={row.id} className="p-3 space-y-2">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-bold text-slate-800 text-sm">{row.name}</p>
                  <p className="text-[11px] text-slate-500">{row.items} آیتم</p>
                </div>
                <p className="font-black text-emerald-800 text-sm shrink-0">{formatCompactMoney(row.calculated)}</p>
              </div>
              <div className="grid grid-cols-2 gap-2 text-[12px]">
                <div className="rounded-lg bg-slate-50 px-2.5 py-2">
                  <p className="text-slate-500">پایه</p>
                  <p className="font-bold text-slate-800">{formatCompactMoney(row.base)}</p>
                </div>
                <div className="rounded-lg bg-violet-50 px-2.5 py-2">
                  <p className="text-violet-700">بیمه {row.insurancePct > 0 ? `(${formatPercentDisplay(row.insurancePct, 1)}٪)` : ''}</p>
                  <p className="font-bold text-violet-900">{formatCompactMoney(row.insurance)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-emerald-700" style={{ width: `${Math.min(100, row.share)}%` }} />
                </div>
                <span className="font-mono text-xs w-12 shrink-0">{formatPercentDisplay(row.share, 1)}٪</span>
              </div>
            </div>
          ))}
          {insight.sections.length === 0 && (
            <p className="p-8 text-center text-slate-400 text-sm">بخشی ثبت نشده است</p>
          )}
        </div>
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-slate-50 text-slate-500 text-xs">
              <tr>
                <th className="p-3 text-right font-bold">بخش</th>
                <th className="p-3 text-right font-bold">آیتم</th>
                <th className="p-3 text-right font-bold">پایه</th>
                <th className="p-3 text-right font-bold">بیمه</th>
                <th className="p-3 text-right font-bold">جمع بخش</th>
                <th className="p-3 text-right font-bold">سهم از پایه</th>
              </tr>
            </thead>
            <tbody>
              {insight.sections.map((row) => (
                <tr key={row.id} className="border-t border-slate-100">
                  <td className="p-3 font-bold text-slate-800">{row.name}</td>
                  <td className="p-3 font-mono">{row.items}</td>
                  <td className="p-3 font-mono">{formatMoney(row.base)}</td>
                  <td className="p-3 font-mono">
                    {formatMoney(row.insurance)}
                    {row.insurancePct > 0 && (
                      <span className="text-[11px] text-slate-500 mr-1">({formatPercentDisplay(row.insurancePct, 2)}٪)</span>
                    )}
                  </td>
                  <td className="p-3 font-mono font-bold text-emerald-800">{formatMoney(row.calculated)}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full bg-emerald-700" style={{ width: `${Math.min(100, row.share)}%` }} />
                      </div>
                      <span className="font-mono text-xs w-12">{formatPercentDisplay(row.share, 1)}٪</span>
                    </div>
                  </td>
                </tr>
              ))}
              {insight.sections.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-slate-400">بخشی ثبت نشده است</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        <section className="xl:col-span-7 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden min-w-0">
          <div className="px-3 sm:px-4 py-3 border-b border-slate-100 flex items-center gap-2">
            <Gauge size={18} className="text-emerald-800 shrink-0" />
            <div className="min-w-0">
              <h3 className="text-sm font-bold text-slate-800">تحلیل حساسیت ضریب</h3>
              <p className="text-[11px] text-slate-500 leading-snug">اگر با ضرایب مختلف روی برآورد اولیه پیشنهاد بدهید، سود خالص چقدر می‌شود</p>
            </div>
          </div>
          {insight.sensitivity.length > 0 ? (
            <>
              <div className="lg:hidden divide-y divide-slate-100">
                {insight.sensitivity.map((row) => {
                  const current = insight.coefficient != null && Math.abs(row.coefficient - insight.coefficient) < 0.02;
                  return (
                    <div
                      key={row.coefficient}
                      className={`p-3 space-y-2 ${current ? 'bg-emerald-50' : ''} ${!row.viable ? 'text-rose-700' : ''}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-black" dir="ltr">
                          ضریب {row.coefficient.toFixed(2)}
                          {current && <span className="mr-2 text-[10px] text-emerald-700">فعلی</span>}
                        </p>
                        <p className="font-bold">{formatCompactMoney(row.netProfit)}</p>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-[11px]">
                        <div>
                          <p className="text-slate-500">پیشنهاد</p>
                          <p className="font-bold">{formatCompactMoney(row.bid)}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">کسورات</p>
                          <p className="font-bold">{formatCompactMoney(row.extra)}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">حاشیه</p>
                          <p className="font-bold">{formatPercentDisplay(row.margin, 1)}٪</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                  <thead className="bg-slate-50 text-xs text-slate-500">
                    <tr>
                      <th className="p-3 text-right font-bold">ضریب</th>
                      <th className="p-3 text-right font-bold">پیشنهاد</th>
                      <th className="p-3 text-right font-bold">سود ضمنی</th>
                      <th className="p-3 text-right font-bold">کسورات</th>
                      <th className="p-3 text-right font-bold">سود خالص</th>
                      <th className="p-3 text-right font-bold">حاشیه</th>
                    </tr>
                  </thead>
                  <tbody>
                    {insight.sensitivity.map((row) => {
                      const current = insight.coefficient != null && Math.abs(row.coefficient - insight.coefficient) < 0.02;
                      return (
                        <tr
                          key={row.coefficient}
                          className={`border-t border-slate-100 ${current ? 'bg-emerald-50' : ''} ${!row.viable ? 'text-rose-700' : ''}`}
                        >
                          <td className="p-3 font-mono font-bold" dir="ltr">
                            {row.coefficient.toFixed(2)}
                            {current && <span className="mr-2 text-[10px] text-emerald-700">فعلی</span>}
                          </td>
                          <td className="p-3 font-mono">{formatMoney(row.bid)}</td>
                          <td className="p-3 font-mono">{formatMoney(row.impliedProfit)}</td>
                          <td className="p-3 font-mono">{formatMoney(row.extra)}</td>
                          <td className="p-3 font-mono font-bold">{formatMoney(row.netProfit)}</td>
                          <td className="p-3 font-mono">{formatPercentDisplay(row.margin, 1)}٪</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="p-8 text-sm text-slate-400 text-center">برای این جدول برآورد اولیه لازم است.</div>
          )}
        </section>

        <section className="xl:col-span-5 space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck size={18} className="text-emerald-800 shrink-0" />
              <h3 className="text-sm font-bold text-slate-800">پیشنهاد مناقصه</h3>
            </div>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">پوشش هزینه + بیمه</dt>
                <dd className="font-mono font-bold shrink-0" dir="ltr">{insight.coverCoefficient != null ? insight.coverCoefficient.toFixed(4) : '—'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">سربه‌سر (سود صفر)</dt>
                <dd className="font-mono font-bold shrink-0" dir="ltr">{insight.breakEvenCoefficient != null ? insight.breakEvenCoefficient.toFixed(4) : '—'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">ضریب فعلی آنالیز</dt>
                <dd className="font-mono font-black text-emerald-800 shrink-0" dir="ltr">{insight.recommendedCoefficient != null ? insight.recommendedCoefficient.toFixed(4) : '—'}</dd>
              </div>
              <div className="flex justify-between gap-3 min-w-0">
                <dt className="text-slate-500 shrink-0">اختلاف با برآورد</dt>
                <dd className={`font-bold text-end min-w-0 ${insight.difference != null && insight.difference < 0 ? 'text-amber-700' : 'text-slate-800'}`}>
                  {insight.difference != null ? (
                    <>
                      <span className="sm:hidden">{formatCompactMoney(insight.difference)}</span>
                      <span className="hidden sm:inline font-mono">{formatMoney(insight.difference)}</span>
                    </>
                  ) : '—'}
                </dd>
              </div>
            </dl>
            <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
              ضریب فعلی همان قیمت پیشنهادی تقسیم بر برآورد کارفرماست. اگر هدف رقابت است، حساسیت جدول را با ضرایب نزدیک سربه‌سر بسنجید.
            </p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-3">
              <Wallet size={18} className="text-emerald-800 shrink-0" />
              <h3 className="text-sm font-bold text-slate-800">نقدینگی و تعهدات</h3>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between gap-3">
                <span className="text-slate-500">پیش‌پرداخت</span>
                <span className="font-bold text-end">
                  {insight.prepaymentTotal > 0 ? <MoneyValue amount={insight.prepaymentTotal} /> : '—'}
                </span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-slate-500">حسن انجام / نگهداری</span>
                <span className="font-bold text-end">
                  {insight.maintenanceRetention > 0 ? <MoneyValue amount={insight.maintenanceRetention} /> : '—'}
                </span>
              </div>
              {insight.adjustmentPct > 0 && (
                <>
                  <div className="flex justify-between gap-3">
                    <span className="text-slate-500">مبلغ با تعدیل {formatPercentDisplay(insight.adjustmentPct, 2)}٪</span>
                    <span className="font-bold text-end"><MoneyValue amount={insight.totalWithAdjustment} /></span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-slate-500">سود خالص با تعدیل</span>
                    <span className="font-bold text-emerald-800 text-end"><MoneyValue amount={insight.netProfitWithAdjustment} /></span>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AnalysisInsights;
